import api from './api'
import {
  AjusteReceita,
  AjusteReceitaRequest,
  AprovarFiscalRequest,
  ConciliacaoNotaRequest,
  ConciliacaoNotaResponse,
  ConferirNotaRequest,
  ConfirmarComissaoRequest,
  LancamentoComissao,
  LancarComissaoRequest,
  MarcoComissao,
  NotaFiscalComissao,
  PoliticaNfRequest,
  PoliticaNfResponse,
  ProgramarComissaoRequest,
  RecorrenciaReserva,
  RegistrarMarcoRequest,
  RegistrarNotaFiscalRequest,
  ResumoComissao,
  StatusComissao,
} from '@/types/comissao'

const BASE_URL = '/api/v1/comissoes'

/** `?a=1&b=2` só com os valores presentes; vazio quando nenhum. */
const consulta = (params: Record<string, string | undefined>): string => {
  const sp = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => v && sp.append(k, v))
  const q = sp.toString()
  return q ? `?${q}` : ''
}

export const comissaoService = {
  /**
   * Extrato: consultor vê as próprias; back-office vê todas, ou as de um consultor com
   * `consultorId` (tela de Consultores). Para o consultor o backend ignora o filtro.
   */
  extrato: async (status?: StatusComissao, consultorId?: string): Promise<LancamentoComissao[]> => {
    return await api.get<LancamentoComissao[]>(`${BASE_URL}/lancamentos${consulta({ status, consultorId })}`)
  },

  resumo: async (consultorId?: string): Promise<ResumoComissao> => {
    return await api.get<ResumoComissao>(`${BASE_URL}/lancamentos/resumo${consulta({ consultorId })}`)
  },

  porReserva: async (oportunidadeId: string): Promise<LancamentoComissao[]> => {
    return await api.get<LancamentoComissao[]>(
      `${BASE_URL}/lancamentos/reserva/${oportunidadeId}`
    )
  },

  // ─── Ações do operador ───

  /**
   * P5 (AN-PP5 §3.1): o corpo NÃO leva `recorrente` — a recorrência é do servidor (política do
   * produto ou fotografia do marco). Corpo montado campo a campo para nada a mais escapar.
   * Erros do backend (mensagem exibida como vem): 422 `POLITICA_COMISSAO_AUSENTE`,
   * 409 `COMPETENCIA_FECHADA`, 409 `COMISSAO_UNICA_JA_LANCADA`, 409 `RECORRENCIA_NAO_ATIVADA`,
   * 422 `COMPETENCIA_FORA_DA_RECORRENCIA`.
   */
  lancar: async (
    oportunidadeId: string,
    request: LancarComissaoRequest
  ): Promise<LancamentoComissao> => {
    return await api.post<LancamentoComissao>(
      `${BASE_URL}/lancamentos/reserva/${oportunidadeId}`,
      {
        competencia: request.competencia,
        valorProjetado: request.valorProjetado,
        competenciaReferencia: request.competenciaReferencia ?? null,
        observacoes: request.observacoes ?? null,
      }
    )
  },

  // ─── P5 — recorrência e marcos (AN-PP5 §3.5, §4) ───

  /**
   * Política projetada, marcos e estado DERIVADO da recorrência da reserva (CONSULTOR+;
   * consultor só a própria carteira, senão 404; `registradoPor` omitido para ele).
   */
  recorrenciaReserva: async (oportunidadeId: string): Promise<RecorrenciaReserva> => {
    return await api.get<RecorrenciaReserva>(
      `${BASE_URL}/reservas/${oportunidadeId}/recorrencia`
    )
  },

  /**
   * ATIVACAO ou ENCERRAMENTO (OPERADOR+ back-office). Erros: 422 `EVENTO_ATIVACAO_DIVERGENTE`,
   * 409 `ATIVACAO_JA_REGISTRADA`, 422 reserva fora de ATIVO/ENCERRADA, data inválida.
   */
  registrarMarco: async (
    oportunidadeId: string,
    request: RegistrarMarcoRequest
  ): Promise<MarcoComissao> => {
    return await api.post<MarcoComissao>(
      `${BASE_URL}/reservas/${oportunidadeId}/marcos`,
      request
    )
  },

  /** REVOGACAO de marco registrado por erro (409 `MARCO_COM_LANCAMENTO_CONFIRMADO`). */
  revogarMarco: async (marcoId: string, motivo: string): Promise<MarcoComissao> => {
    return await api.post<MarcoComissao>(`${BASE_URL}/marcos/${marcoId}/revogar`, { motivo })
  },

  /**
   * `valorConfirmado` ausente mantém a projeção — o caso comum é a apuração bater.
   * `receitaOrigineiConfirmada` é obrigatória (V54; zero aceito): entra na meta de receita
   * pela competência do lançamento. Nunca use valor de simulação.
   */
  confirmar: async (
    lancamentoId: string,
    request: ConfirmarComissaoRequest
  ): Promise<LancamentoComissao> => {
    return await api.post<LancamentoComissao>(
      `${BASE_URL}/lancamentos/${lancamentoId}/confirmar`,
      {
        valorConfirmado: request.valorConfirmado ?? null,
        receitaOrigineiConfirmada: request.receitaOrigineiConfirmada,
        observacoes: request.observacoes ?? null,
      }
    )
  },

  /**
   * Cancelar. Se já confirmada com receita, o backend estorna a receita na
   * `competenciaEstorno` (`YYYY-MM-DD`; ausente = mês corrente) — o mês original fica intacto.
   */
  cancelar: async (
    lancamentoId: string,
    motivo: string,
    competenciaEstorno?: string
  ): Promise<LancamentoComissao> => {
    return await api.post<LancamentoComissao>(
      `${BASE_URL}/lancamentos/${lancamentoId}/cancelar`,
      { motivo, competenciaEstorno: competenciaEstorno ?? null }
    )
  },

  /** Correção append-only da receita Originei de um lançamento confirmado (OPERADOR+, não consultor). */
  ajustarReceita: async (
    lancamentoId: string,
    request: AjusteReceitaRequest
  ): Promise<AjusteReceita> => {
    return await api.post<AjusteReceita>(
      `${BASE_URL}/lancamentos/${lancamentoId}/receita/ajustes`,
      request
    )
  },

  /** Histórico de ajustes da receita (correções e estorno). */
  ajustesReceita: async (lancamentoId: string): Promise<AjusteReceita[]> => {
    return await api.get<AjusteReceita[]>(
      `${BASE_URL}/lancamentos/${lancamentoId}/receita/ajustes`
    )
  },
  /**
   * A15 (V44): o Financeiro programa a data de pagamento — a linha passa a "Programado".
   * `PATCH /v1/comissoes/{id}/programar`, OPERADOR+; `id` é o do lançamento.
   */
  programar: async (
    lancamentoId: string,
    request: ProgramarComissaoRequest
  ): Promise<LancamentoComissao> => {
    return await api.patch<LancamentoComissao>(
      `${BASE_URL}/${lancamentoId}/programar`,
      request
    )
  },

  // ─── Nota fiscal e liquidação ───

  notasFiscais: async (consultorId?: string): Promise<NotaFiscalComissao[]> => {
    return await api.get<NotaFiscalComissao[]>(`${BASE_URL}/notas-fiscais${consulta({ consultorId })}`)
  },

  /** Uma nota com a leitura automática do arquivo (OCR + IA) e as divergências. */
  notaFiscal: async (notaId: string): Promise<NotaFiscalComissao> => {
    return await api.get<NotaFiscalComissao>(`${BASE_URL}/notas-fiscais/${notaId}`)
  },

  /**
   * AN-9 §5: `chaveIdempotencia` vai no header `Idempotency-Key` — a MESMA intenção reenviada
   * (falha de rede, clique repetido) devolve a nota já gravada (200, `replay=true`) sem novo
   * vínculo nem nova leitura; outra requisição com a mesma chave → 409 `IDEMPOTENCIA_CONFLITO`.
   */
  registrarNota: async (
    request: RegistrarNotaFiscalRequest,
    chaveIdempotencia?: string
  ): Promise<NotaFiscalComissao> => {
    const config = chaveIdempotencia
      ? { headers: { 'Idempotency-Key': chaveIdempotencia } }
      : undefined
    return await api.post<NotaFiscalComissao>(`${BASE_URL}/notas-fiscais`, request, config)
  },

  /** Chamado depois do upload do arquivo ao módulo de documentos. */
  anexarDocumentoNota: async (
    notaId: string,
    documentoId: string
  ): Promise<NotaFiscalComissao> => {
    return await api.patch<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/documento?documentoId=${documentoId}`
    )
  },

  liquidarNota: async (
    notaId: string,
    dataPagamento: string,
    comprovanteId?: string
  ): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/liquidar`,
      { dataPagamento, comprovanteId: comprovanteId ?? null, observacoes: null }
    )
  },

  recusarNota: async (notaId: string, motivo: string): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/recusar`,
      { motivo }
    )
  },

  // ─── AN-9 (L10 · Q36) — contrato de docs/agentes/visao-consultor/AN9-nota-fiscal.md ───

  /**
   * Pré-validação do valor digitado contra as comissões marcadas (mesmo escopo do registro;
   * nada é gravado). Soma, diferença e tolerância vêm do backend — a tela não soma.
   */
  conciliarNota: async (request: ConciliacaoNotaRequest): Promise<ConciliacaoNotaResponse> => {
    return await api.post<ConciliacaoNotaResponse>(
      `${BASE_URL}/notas-fiscais/conciliacao`,
      request
    )
  },

  /** Confirmação humana dos dados (Financeiro). ENVIADA/DADOS_CONFIRMADOS → DADOS_CONFIRMADOS. */
  conferirNota: async (
    notaId: string,
    request: ConferirNotaRequest
  ): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/conferir`,
      request
    )
  },

  /** Registro fiscal (só registro; o backend confere bruto − retenções = líquido). */
  aprovarFiscalNota: async (
    notaId: string,
    request: AprovarFiscalRequest
  ): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/aprovar-fiscal`,
      request
    )
  },

  /** APROVADA_FISCAL → DADOS_CONFIRMADOS, com motivo. */
  revogarAprovacaoNota: async (notaId: string, motivo: string): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(
      `${BASE_URL}/notas-fiscais/${notaId}/revogar-aprovacao`,
      { motivo }
    )
  },

  /**
   * Consultor retira a própria nota com pendência aberta para reemitir (Q-AN9-11): equivale a
   * uma recusa "Retirada pelo consultor para reemissão"; as comissões voltam ao disponível.
   */
  retirarNota: async (notaId: string): Promise<NotaFiscalComissao> => {
    return await api.post<NotaFiscalComissao>(`${BASE_URL}/notas-fiscais/${notaId}/retirar`)
  },

  /** Política vigente de NF. Consultor: só tolerância e CNPJ do tomador; back-office: + histórico. */
  politicaNf: async (tenantId?: string): Promise<PoliticaNfResponse> => {
    const sufixo = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''
    return await api.get<PoliticaNfResponse>(`${BASE_URL}/notas-fiscais/politica${sufixo}`)
  },

  /**
   * PROPÕE nova versão da política (FINANCEIRO/GERENTE/ADMIN; V64, AN-PP6 §3): responde 202 com o painel do escopo
   * e a proposta em `pendente` — a tolerância/CNPJ efetivos NÃO mudam até OUTRA pessoa aprovar. 409
   * `PROPOSTA_JA_PENDENTE`; 422 `TOLERANCIA_POR_TENANT_PROIBIDA`, `CNPJ_TOMADOR_OBRIGATORIO`, `TOLERANCIA_OBRIGATORIA`.
   */
  salvarPoliticaNf: async (request: PoliticaNfRequest): Promise<PoliticaNfResponse> => {
    return await api.put<PoliticaNfResponse>(`${BASE_URL}/notas-fiscais/politica`, request)
  },

  /**
   * Aprova a proposta (OUTRA pessoa, FINANCEIRO/ADMIN): a nova vale do instante da aprovação. Devolve o painel. 403
   * `APROVACAO_PELO_PROPONENTE` / `APROVADOR_SEM_PAPEL`; 409 `CONFLITO_VERSAO`, `TRANSICAO_PUBLICACAO_INVALIDA`.
   */
  aprovarPoliticaNf: async (id: string, versaoRegistro?: number | null): Promise<PoliticaNfResponse> => {
    return await api.post<PoliticaNfResponse>(
      `${BASE_URL}/notas-fiscais/politica/${encodeURIComponent(id)}/aprovar`,
      { versaoRegistro: versaoRegistro ?? null }
    )
  },

  /** Rejeita a proposta (motivo obrigatório — 422 `MOTIVO_REJEICAO_OBRIGATORIO`; mesmas regras de quem aprova). */
  rejeitarPoliticaNf: async (
    id: string,
    motivo: string,
    versaoRegistro?: number | null
  ): Promise<PoliticaNfResponse> => {
    return await api.post<PoliticaNfResponse>(
      `${BASE_URL}/notas-fiscais/politica/${encodeURIComponent(id)}/rejeitar`,
      { motivo, versaoRegistro: versaoRegistro ?? null }
    )
  },

  /**
   * PP-final: o PRÓPRIO proponente retira a proposta pendente (→ REJEITADA, motivo "Retirada pelo proponente").
   * Devolve o painel. 403 `RETIRADA_SO_PELO_PROPONENTE`; 409 `TRANSICAO_PUBLICACAO_INVALIDA`.
   */
  retirarPoliticaNf: async (id: string): Promise<PoliticaNfResponse> => {
    return await api.post<PoliticaNfResponse>(`${BASE_URL}/notas-fiscais/politica/${encodeURIComponent(id)}/retirar`)
  },

  // ─── Onda 5 (AN-11 §3.4 — DEV-U, oportunidades V59) ───
  // Nenhum endpoint novo: a verificação do arquivo chega nos campos `documentoVerificacao` e
  // `documentoVerificacaoMotivo` de `NotaFiscalComissaoResponse` (lista e detalhe acima). O
  // mesmo `anexarDocumentoNota` serve para reanexar arquivo recusado (422
  // `NF_DOCUMENTO_INVALIDO` quando o arquivo já projetado não é da nota/dono/tenant);
  // `conferirNota`/`aprovarFiscalNota` respondem 409 `NF_ARQUIVO_NAO_VERIFICADO` /
  // `NF_ARQUIVO_RECUSADO`; `registrarNota` com `documentoId` → 400 `NF_DOCUMENTO_NO_REGISTRO`
  // (a tela nunca envia). Textos em `pages/financeiro/errosNotaFiscal.ts`.
}
